import React, { useEffect, useState } from 'react';
import Layout from '../components/Layout.jsx';
import './Ticketing.css';
import { ticketService } from '../services/apiService';
import { Tab, Tabs, Table, Card, Row, Col, Spinner, Badge } from 'react-bootstrap';

const Ticketing = () => {
  // Ticket counts
  const [counts, setCounts] = useState({
    createTicket: 0,
    uresTicket: 0,
    resTicket: 0,
    closeTicket: 0,
  });
  
  // Ticket lists
  const [allTickets, setAllTickets] = useState([]);
  const [unresolvedTickets, setUnresolvedTickets] = useState([]);
  const [resolvedTickets, setResolvedTickets] = useState([]);
  const [closedTickets, setClosedTickets] = useState([]);
  const [deletedTickets, setDeletedTickets] = useState([]);
  
  // Loading states
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('all');

  // Status colors
  const statusColors = {
    unresolved: '#dc3545',
    resolved: '#28a745',
    closed: '#6c757d',
    deleted: '#6c757d',
    all: '#007bff'
  };

  // Priority badges
  const getPriorityBadge = (priority) => {
    const priorityMap = {
      high: { color: 'danger', text: 'High' },
      medium: { color: 'warning', text: 'Medium' },
      low: { color: 'info', text: 'Low' }
    };
    return priorityMap[priority] || { color: 'secondary', text: 'Normal' };
  };

  // Fetch ticket counts and lists
  useEffect(() => {
    async function fetchData() {
      setLoading(true);
      try {
        const [countAll, countUnres, countRes, countClose, all, unres, res, close, del] = await Promise.all([
          fetch('/count-all-ticket').then(r => r.json()),
          fetch('/count-unres-ticket').then(r => r.json()),
          fetch('/count-res-ticket').then(r => r.json()),
          fetch('/count-close-ticket').then(r => r.json()),
          ticketService.getAllTickets().then(r => r.data),
          ticketService.getUnresolvedTickets().then(r => r.data),
          ticketService.getResolvedTickets().then(r => r.data),
          ticketService.getClosedTickets().then(r => r.data),
          ticketService.getDeletedTickets().then(r => r.data),
        ]);
        
        setCounts({
          createTicket: countAll.createTicket,
          uresTicket: countUnres.uresTicket,
          resTicket: countRes.resTicket,
          closeTicket: countClose.closeTicket,
        });
        
        setAllTickets(all);
        setUnresolvedTickets(unres);
        setResolvedTickets(res);
        setClosedTickets(close);
        setDeletedTickets(del);
      } catch (err) {
        console.error('Error fetching ticket data:', err);
      }
      setLoading(false);
    }
    fetchData();
  }, []);

  // Enhanced table columns with status and priority
  const ticketColumns = [
    'Ticket ID', 'Subject', 'Raised By', 'Assigned To', 'Priority', 'Status', 'Created', 'Last Updated', 'Actions'
  ];

  // Render ticket rows with enhanced styling
  const renderRows = (tickets, status) => tickets.map(ticket => {
    const priority = getPriorityBadge(ticket.priority?.toLowerCase());
    
    return (
      <tr key={ticket.ticket_id} className="ticket-row">
        <td className="fw-semibold">
          <span className="ticket-id">#{ticket.ticket_id}</span>
        </td>
        <td>
          <div className="ticket-subject">
            <strong>{ticket.subject || 'No Subject'}</strong>
            {ticket.remark && (
              <small className="text-muted d-block mt-1">{ticket.remark.substring(0, 60)}...</small>
            )}
          </div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="avatar-sm me-2">
              <div className="avatar-initial bg-primary rounded-circle d-flex align-items-center justify-content-center" 
                   style={{ width: 32, height: 32 }}>
                <span className="text-white fw-medium">
                  {ticket.sender_name?.charAt(0) || 'U'}
                </span>
              </div>
            </div>
            <div>
              <div className="fw-medium">{ticket.sender_name}</div>
              <small className="text-muted">{ticket.sender_email}</small>
            </div>
          </div>
        </td>
        <td>
          <div className="d-flex align-items-center">
            <div className="avatar-sm me-2">
              <div className="avatar-initial bg-success rounded-circle d-flex align-items-center justify-content-center" 
                   style={{ width: 32, height: 32 }}>
                <span className="text-white fw-medium">
                  {ticket.receiver_name?.charAt(0) || 'A'}
                </span>
              </div>
            </div>
            <div>
              <div className="fw-medium">{ticket.receiver_name}</div>
              <small className="text-muted">{ticket.receiver_role}</small>
            </div>
          </div>
        </td>
        <td>
          <Badge bg={priority.color} className="priority-badge">
            {priority.text}
          </Badge>
        </td>
        <td>
          <Badge 
            bg="light" 
            className={`status-badge ${status}`}
            style={{ 
              color: statusColors[status],
              border: `1px solid ${statusColors[status]}`,
              backgroundColor: `${statusColors[status]}10`
            }}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
          </Badge>
        </td>
        <td>
          <div>
            <div>{ticket.ticket_datetime?.split('T')[0] || '-'}</div>
            <small className="text-muted">
              {ticket.ticket_datetime?.split('T')[1]?.substring(0, 5) || ''}
            </small>
          </div>
        </td>
        <td>
          <div>
            <div>{ticket.updated_at?.split('T')[0] || '-'}</div>
            <small className="text-muted">
              {ticket.updated_at?.split('T')[1]?.substring(0, 5) || ''}
            </small>
          </div>
        </td>
        <td>
          <div className="d-flex gap-2">
            <button className="btn btn-sm btn-outline-primary">
              <i className="bi bi-eye"></i>
            </button>
            <button className="btn btn-sm btn-outline-secondary">
              <i className="bi bi-chat"></i>
            </button>
          </div>
        </td>
      </tr>
    );
  });

  // Format numbers with commas
  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  return (
    <Layout>
      <div className="ticketing-container">
        {/* Header Section */}
        <div className="ticketing-header mb-4">
          <div className="d-flex justify-content-between align-items-center">
            <div>
              <h1 className="page-title">Tickets</h1>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <Row className="stats-row mb-5">
          <Col lg={3} md={6}>
            <Card className="stat-card total-tickets">
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="card-subtitle mb-2">Total Tickets</h6>
                  <h2 className="stat-number mb-0">{formatNumber(counts.createTicket)}</h2>
                  <small className="text-muted">All time</small>
                </div>
                <div className="stat-icon">
                  <i className="bi bi-ticket-perforated" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="stat-card unresolved-tickets">
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="card-subtitle mb-2">Unresolved</h6>
                  <h2 className="stat-number mb-0">{formatNumber(counts.uresTicket)}</h2>
                  <small className="text-danger">
                    <i className="bi bi-arrow-up me-1"></i>
                    Requires attention
                  </small>
                </div>
                <div className="stat-icon">
                  <i className="bi bi-envelope-exclamation" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="stat-card resolved-tickets">
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="card-subtitle mb-2">Resolved</h6>
                  <h2 className="stat-number mb-0">{formatNumber(counts.resTicket)}</h2>
                  <small className="text-success">
                    <i className="bi bi-check-circle me-1"></i>
                    Completed
                  </small>
                </div>
                <div className="stat-icon">
                  <i className="bi bi-envelope-check" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
          <Col lg={3} md={6}>
            <Card className="stat-card closed-tickets">
              <Card.Body className="d-flex align-items-center justify-content-between">
                <div>
                  <h6 className="card-subtitle mb-2">Closed</h6>
                  <h2 className="stat-number mb-0">{formatNumber(counts.closeTicket)}</h2>
                  <small className="text-muted">Archived</small>
                </div>
                <div className="stat-icon">
                  <i className="bi bi-journal-check" style={{ fontSize: '2.5rem' }}></i>
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Main Content Card */}
        <Card className="tickets-card">
          <Card.Header className="d-flex justify-content-between align-items-center py-3">
            <div>
              <h5 className="mb-0">Ticket List</h5>
              <small className="text-muted">
                Showing {allTickets.length} tickets across all statuses
              </small>
            </div>
            <div className="d-flex gap-2 align-items-center">
              <div className="input-group input-group-sm" style={{ width: 250 }}>
                <span className="input-group-text">
                  <i className="bi bi-search"></i>
                </span>
                <input 
                  type="text" 
                  className="form-control" 
                  placeholder="Search tickets..." 
                />
              </div>
              <select className="form-select form-select-sm" style={{ width: 150 }}>
                <option>Filter by</option>
                <option>Priority</option>
                <option>Date</option>
                <option>Assignee</option>
              </select>
            </div>
          </Card.Header>
          
          <Card.Body className="p-0">
            {/* Tabs */}
            <div className="border-bottom">
              <Tabs 
                activeKey={tab} 
                onSelect={setTab} 
                className="custom-tabs px-3 pt-2"
              >
                <Tab 
                  eventKey="all" 
                  title={
                    <div className="tab-title">
                      <span className="tab-icon">
                        <i className="bi bi-grid"></i>
                      </span>
                      All
                      <Badge bg="primary" pill className="ms-2">
                        {counts.createTicket}
                      </Badge>
                    </div>
                  }
                />
                <Tab 
                  eventKey="unresolved" 
                  title={
                    <div className="tab-title">
                      <span className="tab-icon">
                        <i className="bi bi-clock"></i>
                      </span>
                      Unresolved
                      <Badge bg="danger" pill className="ms-2">
                        {counts.uresTicket}
                      </Badge>
                    </div>
                  }
                />
                <Tab 
                  eventKey="resolved" 
                  title={
                    <div className="tab-title">
                      <span className="tab-icon">
                        <i className="bi bi-check-circle"></i>
                      </span>
                      Resolved
                      <Badge bg="success" pill className="ms-2">
                        {counts.resTicket}
                      </Badge>
                    </div>
                  }
                />
                <Tab 
                  eventKey="closed" 
                  title={
                    <div className="tab-title">
                      <span className="tab-icon">
                        <i className="bi bi-archive"></i>
                      </span>
                      Closed
                      <Badge bg="secondary" pill className="ms-2">
                        {counts.closeTicket}
                      </Badge>
                    </div>
                  }
                />
                <Tab 
                  eventKey="deleted" 
                  title={
                    <div className="tab-title">
                      <span className="tab-icon">
                        <i className="bi bi-trash"></i>
                      </span>
                      Deleted
                      <Badge bg="dark" pill className="ms-2">
                        {deletedTickets.length}
                      </Badge>
                    </div>
                  }
                />
              </Tabs>
            </div>

            {/* Table Content */}
            <div className="table-container">
              {loading ? (
                <div className="loading-state text-center py-5">
                  <Spinner animation="border" role="status" className="me-2" />
                  <span>Loading tickets...</span>
                </div>
              ) : (
                <div className="table-responsive-scroll" style={{ maxHeight: "calc(100vh - 400px)", overflowY: "auto" }}>
                  <Table hover className="tickets-table mb-0">
                    <thead className="table-light">
                      <tr>
                        {ticketColumns.map(col => (
                          <th key={col} className={col === 'Actions' ? 'text-end' : ''}>
                            {col}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {tab === 'all' && renderRows(allTickets, 'all')}
                      {tab === 'unresolved' && renderRows(unresolvedTickets, 'unresolved')}
                      {tab === 'resolved' && renderRows(resolvedTickets, 'resolved')}
                      {tab === 'closed' && renderRows(closedTickets, 'closed')}
                      {tab === 'deleted' && renderRows(deletedTickets, 'deleted')}
                    </tbody>
                  </Table>
                  
                  {/* Empty State */}
                  {(tab === 'all' && allTickets.length === 0) && (
                    <div className="empty-state text-center py-5">
                      <i className="bi bi-ticket text-muted" style={{ fontSize: '3rem' }}></i>
                      <h5 className="mt-3">No tickets found</h5>
                      <p className="text-muted">Create your first ticket to get started</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card.Body>
          
          {/* Table Footer */}
          <Card.Footer className="d-flex justify-content-between align-items-center py-3">
            <div className="text-muted">
              Showing {allTickets.length} of {counts.createTicket} tickets
            </div>
            <div className="d-flex gap-2">
              <button className="btn btn-sm btn-outline-secondary" disabled>
                <i className="bi bi-chevron-left"></i>
              </button>
              <button className="btn btn-sm btn-outline-secondary">
                <i className="bi bi-chevron-right"></i>
              </button>
            </div>
          </Card.Footer>
        </Card>
      </div>
    </Layout>
  );
};

export default Ticketing;